import React, { useState, useEffect } from 'react';
import {
  AlertTriangle, CheckCircle, Target, ArrowRight,
  BookOpen, Award, ChevronDown, ChevronUp,
  Wind, XCircle, Pause, Activity, Play, ChevronRight, Clock,
} from 'lucide-react';
import { getCatIcon } from './DashboardShared';

/* ── Next Task Countdown Timer ── */
const NextTaskCountdown = () => {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calc = () => {
      const now = new Date();
      // Türkiye saati ile gece yarısını hesapla (UTC+3)
      const trNow = new Date(now.getTime() + (3 * 60 * 60 * 1000) + (now.getTimezoneOffset() * 60 * 1000));
      const trTomorrow = new Date(trNow);
      trTomorrow.setDate(trTomorrow.getDate() + 1);
      trTomorrow.setHours(0, 0, 0, 0);
      const diff = Math.max(0, Math.floor((trTomorrow - trNow) / 1000));
      setTimeLeft({
        hours: Math.floor(diff / 3600),
        minutes: Math.floor((diff % 3600) / 60),
        seconds: diff % 60,
      });
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, []);

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <div className="flex items-center gap-3 p-3 rounded-xl" style={{
      background: 'linear-gradient(135deg, rgba(168,85,247,0.06), rgba(56,189,248,0.04))',
      border: '1px solid rgba(168,85,247,0.15)',
    }} data-testid="next-task-countdown">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{
        background: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.25)',
      }}>
        <Clock className="w-4 h-4" style={{ color: '#C084FC' }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-medium" style={{ color: '#94A3B8' }}>Yeni görev açılışına</p>
        <div className="flex items-baseline gap-1 mt-0.5">
          <span className="text-lg font-bold tabular-nums" style={{ color: '#C084FC', letterSpacing: '0.5px' }}>
            {pad(timeLeft.hours)}
          </span>
          <span className="text-[10px]" style={{ color: '#64748B' }}>sa</span>
          <span className="text-lg font-bold tabular-nums" style={{ color: '#C084FC', letterSpacing: '0.5px' }}>
            {pad(timeLeft.minutes)}
          </span>
          <span className="text-[10px]" style={{ color: '#64748B' }}>dk</span>
          <span className="text-lg font-bold tabular-nums" style={{ color: '#C084FC', letterSpacing: '0.5px' }}>
            {pad(timeLeft.seconds)}
          </span>
          <span className="text-[10px]" style={{ color: '#64748B' }}>sn</span>
        </div>
      </div>
      <div className="h-8 w-[3px] rounded-full overflow-hidden shrink-0" style={{ background: 'rgba(168,85,247,0.1)' }}>
        <div className="w-full rounded-full transition-all duration-1000" style={{
          height: `${((timeLeft.hours * 3600 + timeLeft.minutes * 60 + timeLeft.seconds) / 86400) * 100}%`,
          background: 'linear-gradient(180deg, #A855F7, #C084FC)',
          boxShadow: '0 0 6px rgba(168,85,247,0.5)',
        }} />
      </div>
    </div>
  );
};

const statusLabels = {
  completed: { label: 'Tamamlandi', color: '#34D399', icon: CheckCircle },
  partial: { label: 'Kismen', color: '#FBBF24', icon: Activity },
  skipped: { label: 'Yapilmadi', color: '#EF4444', icon: XCircle },
  postponed: { label: 'Ertelendi', color: '#A78BFA', icon: Pause },
};

const DailyTaskCard = ({
  taskLoading, todayTask, userState,
  completionDone, selectedStatus, setSelectedStatus,
  completionMsg, showCompletion, setShowCompletion,
  taskExpanded, setTaskExpanded,
  userNote, setUserNote, followupAnswer, setFollowupAnswer,
  skipReason, setSkipReason,
  completing, submitCompletion,
  setShowTaskPlayer,
}) => {
  const task = todayTask?.task_detail;
  const CatIcon = task ? getCatIcon(task.category) : Target;
  const catColor = task?.category_color || '#22D3EE';

  return (
    <div id="daily-task-section" data-testid="daily-task-full-section">
      {taskLoading ? (
        <div className="rounded-[20px] p-8 flex items-center justify-center" style={{ background: 'rgba(11,18,32,0.4)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="w-5 h-5 border-2 border-yellow-400/40 border-t-yellow-400 rounded-full animate-spin" />
          <span className="ml-3 text-sm" style={{ color: '#AAB4C5' }}>Görev hazırlanıyor...</span>
        </div>
      ) : todayTask && task ? (
        <div className="relative" data-testid="daily-task-card">
          <div className="card-task">
            <div className="card-task-border" />
            <div className="card-task-glow" />
            <div className="card-task-stars" />
            <div className="card-task-lightline" />

            <div style={{ position: 'relative', zIndex: 10, padding: '26px' }}>
              {/* Warning */}
              {userState?.risk_flags?.some(f => f.type === 'task_dropout') && !completionDone && (
                <div className="mb-5 p-3 rounded-xl flex items-start gap-2" style={{ background: 'rgba(246,195,67,0.05)', border: '1px solid rgba(246,195,67,0.12)' }} data-testid="task-warning-banner">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: '#F6C343' }} />
                  <p className="text-xs" style={{ color: '#D6DCE8' }}>{userState.risk_flags.find(f => f.type === 'task_dropout')?.message}</p>
                </div>
              )}

              {/* Top bar */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{
                    background: 'rgba(246,195,67,0.15)', border: '1px solid rgba(246,195,67,0.3)',
                  }}>
                    <CatIcon className="w-4 h-4" style={{ color: '#F6C343' }} />
                  </div>
                  <span style={{ color: '#D6DCE8', fontSize: '15px', fontWeight: 600, letterSpacing: '0.2px' }} data-testid="task-topbar-title">
                    Bugunku Görev: <span style={{ color: '#F8FAFC', fontWeight: 700 }}>{task.title}</span>
                  </span>
                </div>
                <div className="flex items-center gap-2.5">
                  {completionDone && selectedStatus && statusLabels[selectedStatus] && (
                    <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold" style={{
                      background: `${statusLabels[selectedStatus].color}15`, color: statusLabels[selectedStatus].color,
                      border: `1px solid ${statusLabels[selectedStatus].color}30`,
                    }} data-testid="task-completion-badge">
                      {React.createElement(statusLabels[selectedStatus].icon, { className: 'w-3 h-3' })}
                      {statusLabels[selectedStatus].label}
                    </span>
                  )}
                  <span style={{ color: '#AAB4C5', fontSize: '13px', fontWeight: 400, letterSpacing: '0.2px' }} data-testid="task-duration">Aktif {task.estimated_duration}</span>
                </div>
              </div>

              {/* Completion message */}
              {completionDone && completionMsg && (
                <div className="mb-5 p-3 rounded-xl flex items-center gap-2" style={{ background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.15)' }} data-testid="task-completion-message">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  <p className="text-emerald-300 text-xs">{completionMsg}</p>
                </div>
              )}

              {/* Next task countdown — shown when task is completed */}
              {completionDone && <div className="mb-5"><NextTaskCountdown /></div>}

              <h2 className="mb-3" style={{ fontSize: '22px', fontWeight: 600, lineHeight: 1.2, letterSpacing: '0.2px', color: '#FFFFFF' }} data-testid="task-heading">Bugün küçük bir görev!</h2>

              <p className="mb-5" data-testid="task-short-desc" style={{ color: '#D6DCE8', fontSize: '15px', fontWeight: 400, lineHeight: 1.65, letterSpacing: '0.2px', maxWidth: '65ch' }}>
                {todayTask.ai_why}
              </p>

              {/* How section */}
              <div className="section-how mb-5" data-testid="task-how-section">
                <div className="section-how-title mb-3">
                  <BookOpen className="w-4 h-4" />
                  <span style={{ fontSize: '14px' }}>Nasıl Yapacaksın?</span>
                </div>
                <p style={{ color: '#D6DCE8', fontSize: '15px', fontWeight: 400, lineHeight: 1.7, letterSpacing: '0.2px' }}>
                  {todayTask.ai_how}
                </p>
              </div>

              {/* Expand button */}
              <button onClick={() => setTaskExpanded(!taskExpanded)}
                className="flex items-center justify-center gap-2 mb-5 rounded transition-all duration-300 hover:scale-[1.02]"
                style={{
                  padding: '10px 22px',
                  background: taskExpanded ? 'rgba(255,200,80,0.12)' : 'rgba(255,200,80,0.06)',
                  border: `1px solid rgba(255,200,80,${taskExpanded ? '0.5' : '0.3'})`,
                  color: '#FCD34D',
                  fontSize: '13px', fontWeight: 600, letterSpacing: '0.2px',
                  boxShadow: taskExpanded
                    ? '0 0 18px rgba(255,200,80,0.5), inset 0 0 12px rgba(255,200,80,0.12)'
                    : '0 0 10px rgba(255,200,80,0.25), inset 0 0 8px rgba(255,200,80,0.06)',
                }}
                data-testid="task-expand-button">
                {taskExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {taskExpanded ? 'Gizle' : 'Devamini gor'}
              </button>

              {taskExpanded && (
                <div className="space-y-4 mb-5 task-expand-enter" data-testid="task-details-expanded">
                  <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <h4 className="text-[10px] font-bold uppercase tracking-widest mb-3 flex items-center gap-2" style={{ color: '#AAB4C5' }}>
                      <Target className="w-3.5 h-3.5" style={{ color: '#FCD34D' }} /> Adimlar
                    </h4>
                    <div className="space-y-2.5">
                      {(task.instructions || []).map((step, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <span className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold mt-0.5" style={{
                            background: 'rgba(246,195,67,0.1)', color: '#FCD34D', border: '1px solid rgba(246,195,67,0.2)',
                          }}>{i + 1}</span>
                          <span style={{ color: '#D6DCE8', fontSize: '14px', letterSpacing: '0.2px' }}>{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="p-4 rounded-xl flex items-start gap-3" style={{ background: 'rgba(52,211,153,0.03)', border: '1px solid rgba(52,211,153,0.08)' }}>
                    <Award className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <div>
                      <span className="text-emerald-300 text-[10px] font-bold uppercase tracking-wider">Fayda</span>
                      <p className="text-xs mt-1" style={{ color: '#AAB4C5' }}>{task.expected_benefit}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Separator */}
              <div style={{ height: '1px', marginBottom: '20px', background: 'linear-gradient(90deg, transparent, rgba(255,200,80,0.15), transparent)' }} />

              {/* Follow-up question */}
              {task.followup_question && !completionDone && (
                <p className="mb-4" style={{ color: '#D6DCE8', fontSize: '14px', lineHeight: 1.65, letterSpacing: '0.2px' }} data-testid="task-followup-question">{task.followup_question}</p>
              )}

              {/* Action buttons */}
              {!completionDone ? (
                <div data-testid="task-completion-form">
                  <button
                    onClick={() => setShowTaskPlayer(true)}
                    className="w-full flex items-center justify-center gap-2.5 rounded transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] mb-3"
                    style={{
                      minHeight: '52px',
                      background: `linear-gradient(135deg, ${catColor}, ${catColor}CC)`,
                      color: '#FFFFFF',
                      fontSize: '15px', fontWeight: 700, letterSpacing: '0.2px',
                      boxShadow: `0 8px 30px ${catColor}40, 0 0 60px ${catColor}10`,
                      border: '1px solid rgba(255,255,255,0.12)',
                    }}
                    data-testid="task-start-player"
                  >
                    <Play className="w-5 h-5" /> Şimdi Başla
                  </button>

                  <button
                    onClick={() => setShowCompletion(!showCompletion)}
                    className="w-full py-2 text-center text-xs transition-all"
                    style={{ color: '#7A839A', fontWeight: 500 }}
                    data-testid="task-already-done"
                  >
                    {showCompletion ? 'Gizle' : 'Zaten yaptım — durumu işle'}
                  </button>

                  {showCompletion && (
                    <div className="mt-3 animate-fadeIn">
                      <div className="grid grid-cols-3 gap-2.5 mb-4">
                        <button onClick={() => setSelectedStatus('completed')}
                          className={`btn-task-done${selectedStatus === 'completed' ? ' btn-active' : ''} flex items-center justify-center gap-1.5 rounded transition-all duration-200 hover:scale-[1.02]`}
                          style={{ minHeight: '46px', fontSize: '14px', fontWeight: 600, letterSpacing: '0.2px' }}
                          data-testid="task-status-completed">
                          Yaptim
                        </button>
                        <button onClick={() => setSelectedStatus('partial')}
                          className={`btn-task-partial${selectedStatus === 'partial' ? ' btn-active' : ''} flex items-center justify-center gap-1.5 rounded transition-all duration-200 hover:scale-[1.02]`}
                          style={{ minHeight: '46px', fontSize: '14px', fontWeight: 600, letterSpacing: '0.2px' }}
                          data-testid="task-status-partial">
                          Kismen yaptim
                        </button>
                        <button onClick={() => setSelectedStatus('skipped')}
                          className={`btn-task-skip${selectedStatus === 'skipped' ? ' btn-active' : ''} flex items-center justify-center gap-1.5 rounded transition-all duration-200 hover:scale-[1.02]`}
                          style={{ minHeight: '46px', fontSize: '14px', fontWeight: 600, letterSpacing: '0.2px' }}
                          data-testid="task-status-skipped">
                          Yapmadim <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      {(selectedStatus === 'skipped' || selectedStatus === 'postponed') && (
                        <div className="flex flex-wrap gap-2 mb-4" data-testid="skip-reason-section">
                          {[{ value: 'forgot', label: 'Unuttum' }, { value: 'no_time', label: 'Vaktim olmadi' }, { value: 'too_hard', label: 'Zor geldi' }].map(r => (
                            <button key={r.value} onClick={() => setSkipReason(r.value)}
                              className="px-3.5 py-1.5 rounded text-[11px] font-medium transition-all duration-200"
                              style={{
                                background: skipReason === r.value ? 'rgba(246,195,67,0.06)' : 'rgba(255,255,255,0.02)',
                                border: `1px solid ${skipReason === r.value ? 'rgba(246,195,67,0.25)' : 'rgba(255,255,255,0.04)'}`,
                                color: skipReason === r.value ? '#FCD34D' : '#7A839A',
                              }} data-testid={`skip-reason-${r.value}`}>{r.label}</button>
                          ))}
                        </div>
                      )}

                      <textarea value={userNote} onChange={(e) => setUserNote(e.target.value)}
                        placeholder="Not ekle (opsiyonel)" rows={2}
                        className="w-full rounded px-4 py-3 outline-none resize-none transition-all duration-200 mb-4"
                        style={{
                          background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(255,255,255,0.08)',
                          color: '#F8FAFC', fontSize: '14px', letterSpacing: '0.2px',
                        }}
                        onFocus={(e) => { e.target.style.borderColor = 'rgba(255,200,80,0.3)'; e.target.style.boxShadow = '0 0 15px rgba(255,200,80,0.05)'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; e.target.style.boxShadow = 'none'; }}
                        data-testid="task-user-note" />

                      <button onClick={() => selectedStatus && submitCompletion(selectedStatus)}
                        disabled={!selectedStatus || completing}
                        className="w-full flex items-center justify-center gap-1.5 rounded transition-all duration-200 disabled:opacity-30 hover:scale-[1.01]"
                        style={{
                          minHeight: '46px',
                          background: selectedStatus ? 'linear-gradient(135deg, #60A5FA 0%, #8B5CF6 100%)' : 'linear-gradient(180deg, rgba(30,41,59,0.55), rgba(15,23,42,0.65))',
                          color: selectedStatus ? '#FFFFFF' : '#64748B',
                          fontSize: '14px', fontWeight: 700, letterSpacing: '0.2px',
                          boxShadow: selectedStatus ? '0 0 20px rgba(96,165,250,0.3), 0 0 15px rgba(139,92,246,0.2)' : 'none',
                          border: `1px solid ${selectedStatus ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.06)'}`,
                        }}
                        data-testid="task-submit-completion">
                        {completing ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <>Onayla <ChevronRight className="w-3.5 h-3.5" /></>}
                      </button>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-[20px] p-8 text-center" style={{
          background: 'linear-gradient(155deg, rgba(14,23,48,0.4) 0%, rgba(19,35,71,0.4) 40%, rgba(11,18,32,0.4) 100%)',
          border: '1px solid rgba(246,195,67,0.08)',
          backdropFilter: 'blur(10px)',
        }}>
          <Wind className="w-8 h-8 mx-auto mb-2" style={{ color: '#2A3555' }} />
          <p className="text-sm mb-4" style={{ color: '#7A839A' }}>Bugün için görev bulunamadı.</p>
          <NextTaskCountdown />
        </div>
      )}
    </div>
  );
};

export default DailyTaskCard;
